import React, { useRef, useEffect, useCallback } from "../../../__compat__/react/main.entry.mjs.mjs";
import { useSpacing } from "../../../api/spacing/useSpacing.mjs.mjs";
import { FormHelperText } from "../../FormHelperText/index.mjs.mjs";
import { FormLabel } from "../../FormLabel/index.mjs.mjs";
import { StyledCheckbox } from "../Checkbox/Checkbox.styles.mjs.mjs";
import { StyledRadioButton } from "../RadioButton/RadioButton.styles.mjs.mjs";
import { StyledContainer, StyledLabelWrapper, StyledDescriptionContainer, } from "./GenericOption.styles.mjs.mjs";
function preventInteraction(event) {
    event.preventDefault();
    event.stopPropagation();
    return false;
}
const noop = () => { };
export const GenericOption = React.forwardRef(function Options({ autoComplete, onBlur = noop, onChange = noop, onFocus = noop, 'data-testid': testId, checked, children: label, className, defaultChecked, disabled = false, validationMessage, hideLabelOptional, hint, id, inputId, inputRef: inputRefCallback = noop, invalid = false, labelAs = 'label', name, readOnly = false, required = false, type, value, ...restProps }, ref) {
    const inputRef = useRef(null);
    const hintMessageId = hint && `${inputId}__hint-message`;
    const validationMessageId = validationMessage && `${inputId}__validation-message`;
    const Input = type === 'checkbox' ? StyledCheckbox : StyledRadioButton;
    /* Prevents the case where disabled and readOnly could happen simultaneously */
    const resolvedReadOnly = disabled ? false : readOnly;
    /* Disables optional labeling since a radio should always come in a labeled group */
    const hideOptional = type === 'radio' ? true : required;
    const handleOnChange = useCallback((event) => {
        var _a;
        // Reset potentional previously set custom validity
        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.setCustomValidity('');
        onChange(event);
    }, [onChange]);
    const hasErrorMessage = invalid && validationMessage;
    const ariaDescribedByIds = [hintMessageId, validationMessageId]
        .join(' ')
        .trim();
    /*
     * Setting the validity of the input node based on `props.invalid` using
     * native HTML5 Form Validtaion API
     */
    useEffect(() => {
        if (inputRef.current) {
            // Zero width whitespace is used to mark field as invalid without validation message
            inputRef.current.setCustomValidity(invalid
                ? validationMessage ||
                    inputRef.current.validationMessage ||
                    '\u200B'
                : '');
        }
    }, [invalid, validationMessage]);
    useEffect(() => {
        inputRefCallback(inputRef.current);
    }, [inputRefCallback]);
    return (React.createElement(StyledContainer, { className: className, "data-testid": testId, hasHint: Boolean(hint), hasValidationMessage: invalid && Boolean(validationMessage), id: id, outerSpacing: useSpacing(restProps), ref: ref },
        React.createElement(Input, { autoComplete: autoComplete, "aria-describedby": ariaDescribedByIds, checked: checked, defaultChecked: defaultChecked, disabled: disabled, invalid: invalid, id: inputId, name: name, onBlur: onBlur, 
            /*
             * Prevent checked state to be manipulated while readOnly since native
             * readOnly applies to the not exposed value of the input
             * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/checkbox#readonly
             */
            onChange: resolvedReadOnly ? undefined : handleOnChange, onClick: resolvedReadOnly ? preventInteraction : undefined, onFocus: onFocus, readOnly: resolvedReadOnly, ref: inputRef, required: required, value: value }),
        label && (React.createElement(StyledLabelWrapper, null,
            React.createElement(FormLabel, { as: labelAs, variant: "static", hideLabelOptional: hideLabelOptional, htmlFor: inputId, disabled: disabled, readOnly: readOnly, required: hideOptional, invalid: invalid, empty: !checked }, label))),
        (hint || hasErrorMessage) && (React.createElement(StyledDescriptionContainer, null,
            hint && (React.createElement(FormHelperText, { id: hintMessageId, variant: "hint" }, hint)),
            hasErrorMessage && (React.createElement(FormHelperText, { id: validationMessageId, variant: "invalid", spaceStackStart: hint ? 'xxs' : undefined }, validationMessage))))));
});
//# sourceMappingURL=GenericOption.mjs.mjs.map